import { useEffect, useState, useMemo } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { useMsal } from "@azure/msal-react";
import { Buffer } from 'buffer';
import Brightness4Icon from '@mui/icons-material/Brightness4';
import Brightness7Icon from '@mui/icons-material/Brightness7';
import {
    Box,
    Paper,
    Typography,
    CircularProgress,
    Alert,
    Tabs,
    Tab,
    Accordion,
    AccordionSummary,
    AccordionDetails,
    Chip,
    Stack,
    Grid,
    Button,
    Divider,
    Card,
    CardContent,
    IconButton,
} from "@mui/material";
import {
    ExpandMore,
    ArrowBack,
    CheckCircle,
    Warning,
    Error as ErrorIcon,
    Person,
    Business,
    Home,
    Assignment,
    Description,
    ContentCopy,
    Check,
    Send,
    CheckCircleOutline,
} from "@mui/icons-material";
import { Document, Page, pdfjs } from "react-pdf";
import { AppDispatch, RootState } from "../../configureStore";
import { getAutomationData, RESET_AUTOMATION, testAutomation } from "../../redux/reducer/automation";
import {
    AutomationData,
    BusMessage,
    ParseFormResponse,
    Parse8821Response,
    Parse1177Response,
    DesigneeInfo8821,
    HALCYON_DESIGNEES,
} from "../../interface/automation";
import globalStyles from "../../globalStyles";
import IconMenu from "../atoms/IconMenu";
import Profile from "../molecules/Profile";
import { useTheme } from "../../ThemeContext";

// Configure PDF.js worker

interface TabPanelProps {
    children?: React.ReactNode;
    index: number;
    value: number;
}

const TabPanel = ({ children, value, index, ...other }: TabPanelProps) => (
    <div
        role="tabpanel"
        hidden={value !== index}
        id={`automation-tabpanel-${index}`}
        aria-labelledby={`automation-tab-${index}`}
        {...other}
    >
        {value === index && <Box sx={{ p: 3 }}>{children}</Box>}
    </div>
);

const Automation = () => {
    const dispatch = useDispatch<AppDispatch>();
    const { mode, toggleColorMode } = useTheme();
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const [tabValue, setTabValue] = useState(0);
    const [pageNumber, setPageNumber] = useState(1);
    const [numPages, setNumPages] = useState(0);
    const [token, setToken] = useState<string | undefined>(undefined);
    const [showRawJson, setShowRawJson] = useState<Record<string, boolean>>({});
    const [showPage8821, setShowPage8821] = useState(false);
    const [page8821NumPages, setPage8821NumPages] = useState(0);
    const [page8821PageNumber, setPage8821PageNumber] = useState(1);
    const [documentsReady, setDocumentsReady] = useState(false);
    const [copiedSection, setCopiedSection] = useState<string | null>(null);
    const [testingAutomation, setTestingAutomation] = useState(false);
    const [testResult, setTestResult] = useState<{ success: boolean; message: string } | null>(null);
    const { instance, accounts } = useMsal();
    const request = { scopes: [`api://${import.meta.env.VITE_TOKEN_AUDIENCE}/api`] };

    // Memoize PDF options to prevent re-renders
    const pdfOptions = useMemo(() => ({
        wasmUrl: `https://unpkg.com/pdfjs-dist@${pdfjs.version}/wasm/`,
        cMapUrl: 'https://unpkg.com/pdfjs-dist@4.8.69/cmaps/',
        cMapPacked: true,
    }), []);

    const FICode = searchParams.get("FICode");
    const irsIncomeId = searchParams.get("irsIncomeId");
    const message = searchParams.get("message");

    const automationData = useSelector<RootState, AutomationData | undefined>(
        ({ automation }) => automation.automationData,
    );
    const pdfUrl = useSelector<RootState, string | undefined>(
        ({ automation }) => automation.pdfUrl,
    );
    const page8821Url = useSelector<RootState, string | undefined>(
        ({ automation }) => automation.page8821Url,
    );
    const loading = useSelector<RootState, boolean>(
        ({ automation }) => automation.loading,
    );
    const error = useSelector<RootState, string | undefined>(
        ({ automation }) => automation.error,
    );

    useEffect(() => {
        const getToken = () => {
            instance.acquireTokenSilent({
                ...request,
                account: accounts[0],
            }).then(({ accessToken, idToken }) => {
                sessionStorage.setItem('ACCESS_TOKEN', accessToken);
                sessionStorage.setItem('ID_TOKEN', idToken);
                setToken(accessToken);
                window.dispatchEvent(new Event("token"));

                try {
                    sessionStorage.setItem('ACCESS_EXPIRY', (JSON.parse(Buffer.from(accessToken.split('.')[1], 'base64').toString('ascii')).exp * 1000).toString());
                } catch (err) {
                    console.error(err);
                }
            }).catch((err) => { console.error(err); });
        };
        getToken();
        const intervalId = setInterval(getToken, 300000);

        return () => clearInterval(intervalId);
    }, [accounts, instance, request]);

    useEffect(() => {
        dispatch({ type: RESET_AUTOMATION });

        if (!FICode || !irsIncomeId || !message || !token) {
            return undefined;
        }

        dispatch(
            getAutomationData({
                input: {
                    FICode,
                    irsIncomeId,
                    message,
                },
            }),
        );

        return () => {
            dispatch({ type: RESET_AUTOMATION });
        };
    }, [dispatch, FICode, irsIncomeId, message, token]);

    // Set documents ready when data is loaded and not loading
    useEffect(() => {
        if (!loading && automationData && (pdfUrl || page8821Url)) {
            // Small delay to ensure DOM is ready
            const timer = setTimeout(() => {
                setDocumentsReady(true);
            }, 100);
            return () => clearTimeout(timer);
        }
        return undefined;
    }, [loading, automationData, pdfUrl, page8821Url]);

    if (!FICode || !irsIncomeId || !message) {
        return (
            <Box sx={{ p: 3 }}>
                <Alert severity="error" sx={{ mb: 2 }}>
                    <Typography variant="h6" gutterBottom>
                        Missing Required Parameters
                    </Typography>
                    <Typography>
                        This page requires the following search parameters: FICode, irsIncomeId, and message.
                    </Typography>
                </Alert>
            </Box>
        );
    }

    if (loading) {
        return (
            <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", height: "80vh" }}>
                <CircularProgress size={60} />
            </Box>
        );
    }

    if (error) {
        return (
            <Box sx={{ p: 3 }}>
                <Alert severity="error">
                    <Typography variant="h6" gutterBottom>
                        Error Loading Automation Data
                    </Typography>
                    <Typography>{error}</Typography>
                </Alert>
            </Box>
        );
    }

    if (!automationData) {
        return null;
    }

    const onDocumentLoadSuccess = ({ numPages: num }: { numPages: number }) => {
        setNumPages(num);
    };

    const onPage8821LoadSuccess = ({ numPages: num }: { numPages: number }) => {
        setPage8821NumPages(num);
    };

    const renderForm8821 = (form: Parse8821Response) => {
        const data = form.extractedData;
        const isIndividual = data.taxpayerType === 'individual';

        return (
            <Box>
                <Grid container spacing={2}>
                    <Grid size={12}>
                        <Paper sx={{ p: 2, bgcolor: "grey.50" }}>
                            <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                                Taxpayer Information
                            </Typography>
                            <Stack spacing={1}>
                                <Stack direction="row" alignItems="center" spacing={1}>
                                    {isIndividual ? <Person fontSize="small" /> : <Business fontSize="small" />}
                                    <Typography variant="subtitle2" fontWeight="medium">
                                        {isIndividual ? 'Individual' : 'Business'}
                                    </Typography>
                                </Stack>
                                {isIndividual ? (
                                    <Grid container spacing={1}>
                                        <Grid size={6}>
                                            <Typography variant="caption" color="text.secondary">First Name</Typography>
                                            <Typography variant="body2">{(data as any).firstName}</Typography>
                                        </Grid>
                                        <Grid size={6}>
                                            <Typography variant="caption" color="text.secondary">Last Name</Typography>
                                            <Typography variant="body2">{(data as any).lastName}</Typography>
                                        </Grid>
                                    </Grid>
                                ) : (
                                    <>
                                        <Typography variant="caption" color="text.secondary">Business Name</Typography>
                                        <Typography variant="body2">{(data as any).businessName}</Typography>
                                    </>
                                )}
                                <Typography variant="body2">TIN: {data.tin?.replace(/(\d{3})(\d{2})(\d{4})/, '$1-$2-$3')}</Typography>
                                <Typography variant="body2" fontWeight="medium">Address:</Typography>
                                <Stack spacing={0} sx={{ pl: 1 }}>
                                    {(data.addressLine1 || data.addressline1) && (
                                        <Typography variant="body2">
                                            {data.addressLine1 || data.addressline1}
                                        </Typography>
                                    )}
                                    {((data as any).addressLine2 || (data as any).addressline2) && (
                                        <Typography variant="body2">
                                            {(data as any).addressLine2 || (data as any).addressline2}
                                        </Typography>
                                    )}
                                    {(data.city || data.state || data.zipcode) && (
                                        <Typography variant="body2">
                                            {[data.city, data.state].filter(Boolean).join(', ')}
                                            {data.zipcode && ` ${data.zipcode}`}
                                        </Typography>
                                    )}
                                </Stack>
                                <Stack direction="row" spacing={2}>
                                    <Chip
                                        label={data.signatureStatus ? "Signed" : "Not Signed"}
                                        color={data.signatureStatus ? "success" : "error"}
                                        size="small"
                                        icon={data.signatureStatus ? <CheckCircle /> : <ErrorIcon />}
                                    />
                                    {data.signatureDate && (
                                        <Typography variant="body2">
                                            Date: {data.signatureDate}
                                        </Typography>
                                    )}
                                </Stack>
                            </Stack>
                        </Paper>
                    </Grid>

                    {data.designees && data.designees.length > 0 && (
                        <Grid size={12}>
                            <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                                Designees ({data.designees.length})
                            </Typography>
                            {data.designees.map((designee: DesigneeInfo8821, idx: number) => {
                                // Check if this designee matches any Halcyon designee
                                const halcyonMatch = HALCYON_DESIGNEES.find(
                                    (hd) => {
                                        const designeeName = designee.name?.toLowerCase().trim();
                                        const halcyonName = hd.name?.toLowerCase().trim();
                                        // Remove all non-digits for CAF comparison
                                        const designeeCaf = (designee.caf || designee["CAF No"])?.replace(/\D/g, '');
                                        const halcyonCaf = hd.caf?.replace(/\D/g, '');
                                        const designeePtin = (designee.ptin || designee.PTIN)?.toUpperCase().replace(/[^A-Z0-9]/g, '');
                                        const halcyonPtin = hd.ptin?.toUpperCase().replace(/[^A-Z0-9]/g, '');

                                        return (
                                            designeeName === halcyonName
                                            || (designeeCaf && designeeCaf === halcyonCaf)
                                            || (designeePtin && designeePtin === halcyonPtin)
                                        );
                                    },
                                );

                                const isValidHalcyon = !!halcyonMatch;
                                const hasDiscrepancies = halcyonMatch && (
                                    halcyonMatch.name.toLowerCase().trim() !== designee.name?.toLowerCase().trim()
                                    || halcyonMatch.caf.replace(/\D/g, '') !== (designee.caf || designee["CAF No"])?.replace(/\D/g, '')
                                    || halcyonMatch.ptin.toUpperCase().replace(/[^A-Z0-9]/g, '') !== (designee.ptin || designee.PTIN)?.toUpperCase().replace(/[^A-Z0-9]/g, '')
                                );

                                return (
                                    <Paper
                                        key={idx}
                                        sx={{
                                            p: 1.5,
                                            mb: 1,
                                            border: isValidHalcyon ? '2px solid' : '1px solid',
                                            borderColor: isValidHalcyon
                                                ? (hasDiscrepancies ? 'warning.main' : 'success.main')
                                                : 'error.main',
                                            bgcolor: isValidHalcyon
                                                ? (hasDiscrepancies ? 'warning.50' : 'success.50')
                                                : 'error.50',
                                        }}
                                    >
                                        <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 0.5 }}>
                                            <Typography variant="body2" fontWeight="medium">
                                                {designee.name}
                                            </Typography>
                                            {isValidHalcyon && !hasDiscrepancies && (
                                                <Chip
                                                    label="Valid Halcyon"
                                                    size="small"
                                                    color="success"
                                                    icon={<CheckCircle />}
                                                />
                                            )}
                                            {isValidHalcyon && hasDiscrepancies && (
                                                <Chip
                                                    label="Halcyon (Check Details)"
                                                    size="small"
                                                    color="warning"
                                                    icon={<Warning />}
                                                />
                                            )}
                                            {!isValidHalcyon && (
                                                <Chip
                                                    label="Not Halcyon"
                                                    size="small"
                                                    color="error"
                                                    icon={<ErrorIcon />}
                                                />
                                            )}
                                        </Stack>
                                        <Typography variant="body2" color="text.secondary">
                                            CAF: {designee.caf || designee["CAF No"]} | PTIN: {designee.ptin || designee.PTIN}
                                        </Typography>
                                        {designee.checked !== undefined && (
                                            <Typography variant="body2" color="text.secondary">
                                                Checked: {designee.checked ? "Yes" : "No"}
                                            </Typography>
                                        )}
                                        {halcyonMatch && hasDiscrepancies && (
                                            <Box sx={{ mt: 1, p: 1, bgcolor: 'warning.100', borderRadius: 1 }}>
                                                <Typography variant="caption" color="warning.dark" fontWeight="medium">
                                                    Expected Halcyon Values:
                                                </Typography>
                                                <Typography variant="caption" display="block" color="warning.dark">
                                                    Name: {halcyonMatch.name}
                                                </Typography>
                                                <Typography variant="caption" display="block" color="warning.dark">
                                                    CAF: {halcyonMatch.caf} | PTIN: {halcyonMatch.ptin}
                                                </Typography>
                                            </Box>
                                        )}
                                    </Paper>
                                );
                            })}

                            {/* Show summary of Halcyon designee validation */}
                            <Box sx={{ mt: 2, p: 1.5, bgcolor: 'grey.100', borderRadius: 1 }}>
                                <Typography variant="caption" fontWeight="medium" gutterBottom display="block">
                                    Halcyon Designee Validation Summary:
                                </Typography>
                                <Stack direction="row" spacing={2} sx={{ mt: 1 }}>
                                    <Typography variant="caption">
                                        Expected: {HALCYON_DESIGNEES.length} Halcyon designees
                                    </Typography>
                                    <Typography variant="caption">
                                        Found: {data.designees.filter((d: DesigneeInfo8821) => HALCYON_DESIGNEES.some((hd) => {
                                            const dName = d.name?.toLowerCase().trim();
                                            const hName = hd.name?.toLowerCase().trim();
                                            const dCaf = (d.caf || d["CAF No"])?.replace(/\D/g, '');
                                            const hCaf = hd.caf?.replace(/\D/g, '');
                                            const dPtin = (d.ptin || d.PTIN)?.toUpperCase().replace(/[^A-Z0-9]/g, '');
                                            const hPtin = hd.ptin?.toUpperCase().replace(/[^A-Z0-9]/g, '');
                                            return dName === hName || dCaf === hCaf || dPtin === hPtin;
                                        })).length} matching
                                    </Typography>
                                </Stack>
                                {HALCYON_DESIGNEES.map((halcyon) => {
                                    const found = data.designees.some((d: DesigneeInfo8821) => {
                                        const dName = d.name?.toLowerCase().trim();
                                        const hName = halcyon.name?.toLowerCase().trim();
                                        const dCaf = (d.caf || d["CAF No"])?.replace(/\D/g, '');
                                        const hCaf = halcyon.caf?.replace(/\D/g, '');
                                        const dPtin = (d.ptin || d.PTIN)?.toUpperCase().replace(/[^A-Z0-9]/g, '');
                                        const hPtin = halcyon.ptin?.toUpperCase().replace(/[^A-Z0-9]/g, '');
                                        return dName === hName || dCaf === hCaf || dPtin === hPtin;
                                    });

                                    if (!found) {
                                        return (
                                            <Alert key={halcyon.name} severity="warning" sx={{ mt: 1 }}>
                                                <Typography variant="caption">
                                                    Missing: {halcyon.name} (CAF: {halcyon.caf}, PTIN: {halcyon.ptin})
                                                </Typography>
                                            </Alert>
                                        );
                                    }
                                    return null;
                                })}
                            </Box>
                        </Grid>
                    )}

                    {form.overallConfidence !== undefined && (
                        <Grid size={12}>
                            <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                                Extraction Confidence
                            </Typography>
                            <Stack spacing={1}>
                                <Stack direction="row" alignItems="center" spacing={2}>
                                    <Typography variant="body2">Overall:</Typography>
                                    <Chip
                                        label={`${Math.round(form.overallConfidence)}%`}
                                        color={form.overallConfidence > 0.8 ? "success" : form.overallConfidence > 0.6 ? "warning" : "error"}
                                        size="small"
                                    />
                                </Stack>
                            </Stack>
                        </Grid>
                    )}
                </Grid>
            </Box>
        );
    };

    const renderForm1177 = (form: Parse1177Response) => {
        const dataArray = Array.isArray(form.extractedData) ? form.extractedData : [form.extractedData];

        return (
            <Box>
                {dataArray.map((data: any, idx: number) => (
                    <Paper key={idx} sx={{ p: 2, mb: 1, bgcolor: "grey.50" }}>
                        <Stack spacing={1}>
                            <Grid container spacing={1}>
                                <Grid size={6}>
                                    <Typography variant="caption" color="text.secondary">First Name</Typography>
                                    <Typography variant="body2">{data.firstName}</Typography>
                                </Grid>
                                <Grid size={6}>
                                    <Typography variant="caption" color="text.secondary">Last Name</Typography>
                                    <Typography variant="body2">{data.lastName}</Typography>
                                </Grid>
                            </Grid>
                            <Stack direction="row" spacing={2}>
                                <Chip
                                    label={data.signatureStatus ? "Signed" : "Not Signed"}
                                    color={data.signatureStatus ? "success" : "error"}
                                    size="small"
                                    icon={data.signatureStatus ? <CheckCircle /> : <ErrorIcon />}
                                />
                                {data.signatureDate && (
                                    <Typography variant="body2">
                                        Date: {data.signatureDate}
                                    </Typography>
                                )}
                            </Stack>
                        </Stack>
                    </Paper>
                ))}
            </Box>
        );
    };

    const toggleRawJson = (key: string) => {
        setShowRawJson((prev) => ({ ...prev, [key]: !prev[key] }));
    };

    const copyToClipboard = async (data: any, section: string) => {
        try {
            await navigator.clipboard.writeText(JSON.stringify(data, null, 2));
            setCopiedSection(section);
            setTimeout(() => setCopiedSection(null), 2000);
        } catch (err) {
            console.error('Failed to copy to clipboard:', err);
        }
    };

    const handleTestAutomation = () => {
        if (!automationData?.extracted || !automationData?.body) {
            setTestResult({ success: false, message: 'Missing required data' });
            return;
        }

        setTestingAutomation(true);
        setTestResult(null);

        dispatch(
            testAutomation({
                input: {
                    extracted: automationData.extracted,
                    message: automationData.body,
                },
                onSuccess: (_response) => {
                    setTestingAutomation(false);
                    setTestResult({ success: true, message: 'Test automation completed successfully' });
                },
                onError: (err) => {
                    setTestingAutomation(false);
                    setTestResult({ success: false, message: err?.message || 'Test automation failed' });
                },
            }),
        );
    };

    const renderExtractedData = () => {
        if (!automationData.extracted || automationData.extracted.length === 0) {
            return <Alert severity="info">No extracted data available</Alert>;
        }

        return (
            <Box>
                <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
                    <Typography variant="h6">Extracted Form Data</Typography>
                    <Button
                        size="small"
                        variant="outlined"
                        onClick={() => toggleRawJson('extracted')}
                        startIcon={showRawJson.extracted ? <Description /> : <Description />}
                    >
                        {showRawJson.extracted ? 'Hide' : 'Show'} Raw JSON
                    </Button>
                </Stack>

                {showRawJson.extracted && (
                    <Paper sx={{ p: 2, mb: 3, bgcolor: 'grey.900', position: 'relative' }}>
                        <Button
                            size="small"
                            variant="contained"
                            onClick={() => copyToClipboard(automationData.extracted, 'extracted')}
                            startIcon={copiedSection === 'extracted' ? <Check /> : <ContentCopy />}
                            sx={{
                                position: 'absolute',
                                top: 8,
                                right: 8,
                                bgcolor: copiedSection === 'extracted' ? 'success.main' : 'primary.main',
                                '&:hover': {
                                    bgcolor: copiedSection === 'extracted' ? 'success.dark' : 'primary.dark',
                                },
                            }}
                        >
                            {copiedSection === 'extracted' ? 'Copied!' : 'Copy JSON'}
                        </Button>
                        <Box
                            component="pre"
                            sx={{
                                color: 'common.white',
                                fontSize: '0.875rem',
                                overflow: 'auto',
                                m: 0,
                                pr: 12,
                            }}
                        >
                            {JSON.stringify(automationData.extracted, null, 2)}
                        </Box>
                    </Paper>
                )}
                {automationData.extracted.map((form: ParseFormResponse, index: number) => (
                    <Card key={index} sx={{ mb: 2 }}>
                        <CardContent>
                            <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 2 }}>
                                <Description color="primary" />
                                <Typography variant="h6">
                                    Form {form.type} - Page {form.pageNumber}
                                </Typography>
                                {form.type === "8821" && (form as Parse8821Response).requiresManualReview && (
                                    <Chip
                                        label="Manual Review Required"
                                        color="warning"
                                        size="small"
                                        icon={<Warning />}
                                    />
                                )}
                            </Stack>

                            {form.type === "8821" && renderForm8821(form as Parse8821Response)}
                            {form.type === "1177" && renderForm1177(form as Parse1177Response)}
                        </CardContent>
                    </Card>
                ))}
            </Box>
        );
    };

    const renderApplicantInfo = (body: BusMessage) => (
        <Box>
            <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
                <Typography variant="h6">Applicant Information</Typography>
                <Button
                    size="small"
                    variant="outlined"
                    onClick={() => toggleRawJson('applicant')}
                    startIcon={<Description />}
                >
                    {showRawJson.applicant ? 'Hide' : 'Show'} Raw JSON
                </Button>
            </Stack>

            {showRawJson.applicant && (
                <Paper sx={{ p: 2, mb: 3, bgcolor: 'grey.900', position: 'relative' }}>
                    <Button
                        size="small"
                        variant="contained"
                        onClick={() => copyToClipboard(body, 'applicant')}
                        startIcon={copiedSection === 'applicant' ? <Check /> : <ContentCopy />}
                        sx={{
                            position: 'absolute',
                            top: 8,
                            right: 8,
                            bgcolor: copiedSection === 'applicant' ? 'success.main' : 'primary.main',
                            '&:hover': {
                                bgcolor: copiedSection === 'applicant' ? 'success.dark' : 'primary.dark',
                            },
                        }}
                    >
                        {copiedSection === 'applicant' ? 'Copied!' : 'Copy JSON'}
                    </Button>
                    <Box
                        component="pre"
                        sx={{
                            color: 'common.white',
                            fontSize: '0.875rem',
                            overflow: 'auto',
                            m: 0,
                            pr: 12,
                        }}
                    >
                        {JSON.stringify(body, null, 2)}
                    </Box>
                </Paper>
            )}
            <Grid container spacing={3}>
                <Grid size={12}>
                    <Card>
                        <CardContent>
                            <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 2 }}>
                                <Person color="primary" />
                                <Typography variant="h6">Primary Applicant</Typography>
                            </Stack>
                            <Grid container spacing={2}>
                                <Grid size={4}>
                                    <Typography variant="body2" color="text.secondary">First Name</Typography>
                                    <Typography>{body.firstName}</Typography>
                                </Grid>
                                <Grid size={4}>
                                    <Typography variant="body2" color="text.secondary">Last Name</Typography>
                                    <Typography>{body.lastName}</Typography>
                                </Grid>
                                <Grid size={4}>
                                    <Typography variant="body2" color="text.secondary">TIN</Typography>
                                    <Typography>{body.tin?.replace(/(\d{3})(\d{2})(\d{4})/, '$1-$2-$3')}</Typography>
                                </Grid>
                                {body.businessName && (
                                    <Grid size={12}>
                                        <Typography variant="body2" color="text.secondary">Business</Typography>
                                        <Typography>{body.businessName}</Typography>
                                    </Grid>
                                )}
                            </Grid>
                        </CardContent>
                    </Card>
                </Grid>

                <Grid size={12}>
                    <Card>
                        <CardContent>
                            <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 2 }}>
                                <Home color="primary" />
                                <Typography variant="h6">Address</Typography>
                            </Stack>
                            <Stack spacing={0.5}>
                                {body.addressLine1 && (
                                    <Typography>{body.addressLine1}</Typography>
                                )}
                                {body.addressLine2 && (
                                    <Typography>{body.addressLine2}</Typography>
                                )}
                                {(body.city || body.state || body.zipCode) && (
                                    <Typography>
                                        {[body.city, body.state].filter(Boolean).join(', ')}
                                        {body.zipCode && ` ${body.zipCode}`}
                                    </Typography>
                                )}
                            </Stack>
                        </CardContent>
                    </Card>
                </Grid>

                <Grid size={12}>
                    <Card>
                        <CardContent>
                            <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 2 }}>
                                <Assignment color="primary" />
                                <Typography variant="h6">Loan Information</Typography>
                            </Stack>
                            <Grid container spacing={2}>
                                <Grid size={6}>
                                    <Typography variant="body2" color="text.secondary">Loan Number</Typography>
                                    <Typography>{body.loanNumber}</Typography>
                                </Grid>
                                <Grid size={6}>
                                    <Typography variant="body2" color="text.secondary">Institution</Typography>
                                    <Typography>{body.institutionCode}</Typography>
                                </Grid>
                                <Grid size={6}>
                                    <Typography variant="body2" color="text.secondary">FI Code</Typography>
                                    <Typography>{body.FICode}</Typography>
                                </Grid>
                                <Grid size={6}>
                                    <Typography variant="body2" color="text.secondary">IRS Income ID</Typography>
                                    <Typography>{body.irsIncomeId}</Typography>
                                </Grid>
                            </Grid>
                        </CardContent>
                    </Card>
                </Grid>
            </Grid>
        </Box>
    );

    const renderValidations = () => {
        if (!automationData.validations) {
            return <Alert severity="error">No proper forms found</Alert>;
        }

        const validations = Array.isArray(automationData.validations)
            ? automationData.validations
            : [automationData.validations];

        if (validations.length === 0) {
            return <Alert severity="error">No proper forms found</Alert>;
        }

        return (
            <Box>
                <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
                    <Typography variant="h6">
                        Page Validations ({validations.length})
                    </Typography>
                    <Button
                        size="small"
                        variant="outlined"
                        onClick={() => toggleRawJson('validations')}
                        startIcon={<Description />}
                    >
                        {showRawJson.validations ? 'Hide' : 'Show'} Raw JSON
                    </Button>
                </Stack>

                {showRawJson.validations && (
                    <Paper sx={{ p: 2, mb: 3, bgcolor: 'grey.900', position: 'relative' }}>
                        <Button
                            size="small"
                            variant="contained"
                            onClick={() => copyToClipboard(validations, 'validations')}
                            startIcon={copiedSection === 'validations' ? <Check /> : <ContentCopy />}
                            sx={{
                                position: 'absolute',
                                top: 8,
                                right: 8,
                                bgcolor: copiedSection === 'validations' ? 'success.main' : 'primary.main',
                                '&:hover': {
                                    bgcolor: copiedSection === 'validations' ? 'success.dark' : 'primary.dark',
                                },
                            }}
                        >
                            {copiedSection === 'validations' ? 'Copied!' : 'Copy JSON'}
                        </Button>
                        <Box
                            component="pre"
                            sx={{
                                color: 'common.white',
                                fontSize: '0.875rem',
                                overflow: 'auto',
                                m: 0,
                                pr: 12,
                            }}
                        >
                            {JSON.stringify(validations, null, 2)}
                        </Box>
                    </Paper>
                )}
                {validations.map((validation: any, index: number) => {
                    // Determine validation type and severity
                    const getValidationType = () => {
                        if (validation.field) return validation.field;
                        if (validation.type) return validation.type;
                        if (validation.issue) return 'Issue';
                        if (validation.message) return 'Message';
                        return `Validation ${index + 1}`;
                    };

                    const getSeverity = () => {
                        if (validation.severity) return validation.severity;
                        if (validation.isError || validation.error) return 'error';
                        if (validation.isWarning || validation.warning) return 'warning';
                        return 'info';
                    };

                    const severity = getSeverity();

                    return (
                        <Accordion key={index} sx={{ mb: 1 }}>
                            <AccordionSummary expandIcon={<ExpandMore />}>
                                <Stack direction="row" alignItems="center" spacing={2} sx={{ width: '100%' }}>
                                    <Stack direction="row" alignItems="center" spacing={1} sx={{ flex: 1 }}>
                                        {severity === 'error' && <ErrorIcon color="error" fontSize="small" />}
                                        {severity === 'warning' && <Warning color="warning" fontSize="small" />}
                                        {severity === 'info' && <CheckCircle color="info" fontSize="small" />}
                                        <Typography fontWeight="medium">
                                            {getValidationType()}
                                        </Typography>
                                    </Stack>
                                    <Stack direction="row" spacing={1}>
                                        {validation.pageNumber && (
                                            <Chip label={`Page ${validation.pageNumber}`} size="small" variant="outlined" />
                                        )}
                                        <Chip
                                            label={severity}
                                            size="small"
                                            color={severity === 'error' ? 'error' : severity === 'warning' ? 'warning' : 'info'}
                                        />
                                    </Stack>
                                </Stack>
                            </AccordionSummary>
                            <AccordionDetails>
                                <Stack spacing={2}>
                                    {/* Main validation message */}
                                    {(validation.message || validation.description) && (
                                        <Paper sx={{ p: 2, bgcolor: 'grey.50' }}>
                                            <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                                                Description
                                            </Typography>
                                            <Typography variant="body2">
                                                {validation.message || validation.description}
                                            </Typography>
                                        </Paper>
                                    )}

                                    {/* Validation details in a structured format */}
                                    <Grid container spacing={2}>
                                        {validation.field && (
                                            <Grid size={6}>
                                                <Typography variant="caption" color="text.secondary">Field</Typography>
                                                <Typography variant="body2">{validation.field}</Typography>
                                            </Grid>
                                        )}
                                        {validation.expectedValue !== undefined && (
                                            <Grid size={6}>
                                                <Typography variant="caption" color="text.secondary">Expected</Typography>
                                                <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                                                    {typeof validation.expectedValue === 'object'
                                                        ? JSON.stringify(validation.expectedValue)
                                                        : String(validation.expectedValue)}
                                                </Typography>
                                            </Grid>
                                        )}
                                        {validation.actualValue !== undefined && (
                                            <Grid size={6}>
                                                <Typography variant="caption" color="text.secondary">Actual</Typography>
                                                <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                                                    {typeof validation.actualValue === 'object'
                                                        ? JSON.stringify(validation.actualValue)
                                                        : String(validation.actualValue)}
                                                </Typography>
                                            </Grid>
                                        )}
                                        {validation.value !== undefined && (
                                            <Grid size={6}>
                                                <Typography variant="caption" color="text.secondary">Value</Typography>
                                                <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                                                    {typeof validation.value === 'object'
                                                        ? JSON.stringify(validation.value)
                                                        : String(validation.value)}
                                                </Typography>
                                            </Grid>
                                        )}
                                        {validation.reason && (
                                            <Grid size={12}>
                                                <Typography variant="caption" color="text.secondary">Reason</Typography>
                                                <Typography variant="body2">{validation.reason}</Typography>
                                            </Grid>
                                        )}
                                        {validation.suggestion && (
                                            <Grid size={12}>
                                                <Alert severity="info" sx={{ mt: 1 }}>
                                                    <Typography variant="body2">
                                                        <strong>Suggestion:</strong> {validation.suggestion}
                                                    </Typography>
                                                </Alert>
                                            </Grid>
                                        )}
                                    </Grid>

                                    {/* Show any additional properties not handled above */}
                                    {(() => {
                                        const handledKeys = ['field', 'message', 'description', 'expectedValue', 'actualValue',
                                            'value', 'reason', 'suggestion', 'pageNumber', 'severity',
                                            'type', 'issue', 'isError', 'error', 'isWarning', 'warning'];
                                        const additionalKeys = Object.keys(validation).filter((key) => !handledKeys.includes(key));

                                        if (additionalKeys.length > 0) {
                                            return (
                                                <Accordion sx={{ bgcolor: 'grey.50' }}>
                                                    <AccordionSummary expandIcon={<ExpandMore />}>
                                                        <Typography variant="caption">Additional Details ({additionalKeys.length})</Typography>
                                                    </AccordionSummary>
                                                    <AccordionDetails>
                                                        <Grid container spacing={1}>
                                                            {additionalKeys.map((key) => (
                                                                <Grid size={12} key={key}>
                                                                    <Typography variant="caption" color="text.secondary">
                                                                        {key.replace(/([A-Z])/g, ' $1').replace(/^./, (str) => str.toUpperCase())}
                                                                    </Typography>
                                                                    <Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: '0.875rem' }}>
                                                                        {typeof validation[key] === 'object'
                                                                            ? JSON.stringify(validation[key], null, 2)
                                                                            : String(validation[key])}
                                                                    </Typography>
                                                                </Grid>
                                                            ))}
                                                        </Grid>
                                                    </AccordionDetails>
                                                </Accordion>
                                            );
                                        }
                                        return null;
                                    })()}
                                </Stack>
                            </AccordionDetails>
                        </Accordion>
                    );
                })}
            </Box>
        );
    };

    const renderWarnings = () => {
        if (!automationData.warns || automationData.warns.length === 0) {
            return <Alert severity="info">No warnings found</Alert>;
        }

        return (
            <Box>
                <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
                    <Typography variant="h6">
                        Warnings ({automationData.warns.length})
                    </Typography>
                    <Button
                        size="small"
                        variant="outlined"
                        onClick={() => toggleRawJson('warnings')}
                        startIcon={<Description />}
                    >
                        {showRawJson.warnings ? 'Hide' : 'Show'} Raw JSON
                    </Button>
                </Stack>

                {showRawJson.warnings && (
                    <Paper sx={{ p: 2, mb: 3, bgcolor: 'grey.900', position: 'relative' }}>
                        <Button
                            size="small"
                            variant="contained"
                            onClick={() => copyToClipboard(automationData.warns, 'warnings')}
                            startIcon={copiedSection === 'warnings' ? <Check /> : <ContentCopy />}
                            sx={{
                                position: 'absolute',
                                top: 8,
                                right: 8,
                                bgcolor: copiedSection === 'warnings' ? 'success.main' : 'primary.main',
                                '&:hover': {
                                    bgcolor: copiedSection === 'warnings' ? 'success.dark' : 'primary.dark',
                                },
                            }}
                        >
                            {copiedSection === 'warnings' ? 'Copied!' : 'Copy JSON'}
                        </Button>
                        <Box
                            component="pre"
                            sx={{
                                color: 'common.white',
                                fontSize: '0.875rem',
                                overflow: 'auto',
                                m: 0,
                                pr: 12,
                            }}
                        >
                            {JSON.stringify(automationData.warns, null, 2)}
                        </Box>
                    </Paper>
                )}
                {automationData.warns.map((warning: any, index: number) => (
                    <Card key={index} sx={{ mb: 2, border: 1, borderColor: 'warning.main' }}>
                        <CardContent>
                            <Stack spacing={2}>
                                <Typography variant="subtitle1" color="warning.main" fontWeight="medium">
                                    {warning.issue}
                                </Typography>
                                {warning.actionNeeded && (
                                    <Paper sx={{ p: 2, bgcolor: 'warning.50' }}>
                                        <Typography variant="subtitle2" gutterBottom>
                                            Action Suggested
                                        </Typography>
                                        <Stack spacing={1}>
                                            <Typography variant="body2">
                                                <strong>Action:</strong> {warning.actionNeeded.nextAction}
                                            </Typography>
                                            <Typography variant="body2">
                                                <strong>Message:</strong> {warning.actionNeeded.message}
                                            </Typography>
                                            {!warning.actionNeeded.isBlocking && (
                                                <Chip
                                                    label="Non-Blocking"
                                                    color="info"
                                                    size="small"
                                                    icon={<CheckCircle />}
                                                />
                                            )}
                                        </Stack>
                                    </Paper>
                                )}
                            </Stack>
                        </CardContent>
                    </Card>
                ))}
            </Box>
        );
    };

    const renderErrors = () => {
        if (!automationData.errors || automationData.errors.length === 0) {
            return <Alert severity="success">No errors found</Alert>;
        }

        return (
            <Box>
                <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
                    <Typography variant="h6">
                        Validation Errors ({automationData.errors.length})
                    </Typography>
                    <Button
                        size="small"
                        variant="outlined"
                        onClick={() => toggleRawJson('errors')}
                        startIcon={<Description />}
                    >
                        {showRawJson.errors ? 'Hide' : 'Show'} Raw JSON
                    </Button>
                </Stack>

                {showRawJson.errors && (
                    <Paper sx={{ p: 2, mb: 3, bgcolor: 'grey.900', position: 'relative' }}>
                        <Button
                            size="small"
                            variant="contained"
                            onClick={() => copyToClipboard(automationData.errors, 'errors')}
                            startIcon={copiedSection === 'errors' ? <Check /> : <ContentCopy />}
                            sx={{
                                position: 'absolute',
                                top: 8,
                                right: 8,
                                bgcolor: copiedSection === 'errors' ? 'success.main' : 'primary.main',
                                '&:hover': {
                                    bgcolor: copiedSection === 'errors' ? 'success.dark' : 'primary.dark',
                                },
                            }}
                        >
                            {copiedSection === 'errors' ? 'Copied!' : 'Copy JSON'}
                        </Button>
                        <Box
                            component="pre"
                            sx={{
                                color: 'common.white',
                                fontSize: '0.875rem',
                                overflow: 'auto',
                                m: 0,
                                pr: 12,
                            }}
                        >
                            {JSON.stringify(automationData.errors, null, 2)}
                        </Box>
                    </Paper>
                )}
                {automationData.errors.map((err, index) => (
                    <Card key={index} sx={{ mb: 2, border: 1, borderColor: 'error.main' }}>
                        <CardContent>
                            <Stack spacing={2}>
                                <Typography variant="subtitle1" color="error.main" fontWeight="medium">
                                    {err.issue}
                                </Typography>
                                {err.actionNeeded && (
                                    <Paper sx={{ p: 2, bgcolor: 'error.50' }}>
                                        <Typography variant="subtitle2" gutterBottom>
                                            Action Required
                                        </Typography>
                                        <Stack spacing={1}>
                                            <Typography variant="body2">
                                                <strong>Action:</strong> {err.actionNeeded.nextAction}
                                            </Typography>
                                            <Typography variant="body2">
                                                <strong>Message:</strong> {err.actionNeeded.message}
                                            </Typography>
                                            {err.actionNeeded.isBlocking && (
                                                <Chip
                                                    label="Blocking Issue"
                                                    color="error"
                                                    size="small"
                                                    icon={<ErrorIcon />}
                                                />
                                            )}
                                        </Stack>
                                    </Paper>
                                )}
                            </Stack>
                        </CardContent>
                    </Card>
                ))}
            </Box>
        );
    };

    const renderActionReason = () => {
        if (!automationData.actionReason) {
            return <Alert severity="info">No action reason provided</Alert>;
        }

        const reason = automationData.actionReason;

        return (
            <Box>
                <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
                    <Typography variant="h6">Client Communication</Typography>
                    <Button
                        size="small"
                        variant="outlined"
                        onClick={() => toggleRawJson('action')}
                        startIcon={<Description />}
                    >
                        {showRawJson.action ? 'Hide' : 'Show'} Raw JSON
                    </Button>
                </Stack>

                {showRawJson.action && (
                    <Paper sx={{ p: 2, mb: 3, bgcolor: 'grey.900', position: 'relative' }}>
                        <Button
                            size="small"
                            variant="contained"
                            onClick={() => copyToClipboard(reason, 'action')}
                            startIcon={copiedSection === 'action' ? <Check /> : <ContentCopy />}
                            sx={{
                                position: 'absolute',
                                top: 8,
                                right: 8,
                                bgcolor: copiedSection === 'action' ? 'success.main' : 'primary.main',
                                '&:hover': {
                                    bgcolor: copiedSection === 'action' ? 'success.dark' : 'primary.dark',
                                },
                            }}
                        >
                            {copiedSection === 'action' ? 'Copied!' : 'Copy JSON'}
                        </Button>
                        <Box
                            component="pre"
                            sx={{
                                color: 'common.white',
                                fontSize: '0.875rem',
                                overflow: 'auto',
                                m: 0,
                                pr: 12,
                            }}
                        >
                            {JSON.stringify(reason, null, 2)}
                        </Box>
                    </Paper>
                )}
                <Card sx={{ border: 2, borderColor: reason.isBlocking ? 'error.main' : 'primary.main' }}>
                    <CardContent>
                        <Stack spacing={3}>
                            <Box>
                                <Stack direction="row" alignItems="center" spacing={2} sx={{ mb: 2 }}>
                                    <Typography variant="h6">Action Details</Typography>
                                    {reason.isBlocking && (
                                        <Chip
                                            label="Blocking"
                                            color="error"
                                            icon={<ErrorIcon />}
                                        />
                                    )}
                                </Stack>
                                <Divider sx={{ mb: 2 }} />
                            </Box>

                            <Box>
                                <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                                    Action Code
                                </Typography>
                                <Typography variant="h6" color="primary">
                                    {reason.actionCode}
                                </Typography>
                            </Box>

                            <Box>
                                <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                                    Message to Client
                                </Typography>
                                <Paper sx={{ p: 2, bgcolor: 'grey.50' }}>
                                    <Typography>{reason.message}</Typography>
                                </Paper>
                            </Box>

                            <Grid container spacing={2}>
                                <Grid size={6}>
                                    <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                                        Next Action
                                    </Typography>
                                    <Typography>{reason.nextAction}</Typography>
                                </Grid>
                                <Grid size={6}>
                                    <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                                        Scenario
                                    </Typography>
                                    <Typography>{reason.scenario}</Typography>
                                </Grid>
                            </Grid>

                            <Box>
                                <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                                    Email Configuration
                                </Typography>
                                <Stack spacing={1}>
                                    <Typography variant="body2">
                                        <strong>Subject Tag:</strong> {reason.subjectTag}
                                    </Typography>
                                    <Typography variant="body2">
                                        <strong>Email Scenario:</strong> {reason.emailScenario}
                                    </Typography>
                                    <Typography variant="body2">
                                        <strong>Tag:</strong> {reason.tag}
                                    </Typography>
                                    {reason.faqLink && (
                                        <Chip
                                            label="FAQ Available"
                                            size="small"
                                            color="info"
                                            variant="outlined"
                                        />
                                    )}
                                </Stack>
                            </Box>
                        </Stack>
                    </CardContent>
                </Card>
            </Box>
        );
    };

    return (
        <Box sx={{ display: "flex", flexDirection: "column", minHeight: "100vh" }}>
            {/* Header */}
            <Box sx={[globalStyles.flexCenterBetween, globalStyles.pa1]}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <Box
                        component="img"
                        sx={{
                            height: 40,
                        }}
                        alt="Halcyon Still Water"
                        src={mode === 'dark' ? "/logo-white.png" : "/logo.png"}
                    />
                    <Button
                        startIcon={<ArrowBack />}
                        onClick={() => navigate('/')}
                        variant="outlined"
                        size="small"
                        sx={{
                            borderColor: 'primary.main',
                            color: 'primary.main',
                            '&:hover': {
                                borderColor: 'primary.dark',
                                backgroundColor: 'primary.light',
                            },
                        }}
                    >
                        Back to Home
                    </Button>
                </Box>
                <IconButton sx={{ ml: 1 }} onClick={toggleColorMode} color="inherit">
                    {mode === 'dark' ? <Brightness7Icon /> : <Brightness4Icon />}
                </IconButton>
                <Box>
                    <IconMenu
                        name="AccountCircleOutlined"
                        item={(<Profile user={accounts[0]} />)}
                    />
                </Box>
            </Box>

            {/* Page Content */}
            <Box sx={{ flex: 1, display: "flex", flexDirection: "column" }}>
                <Box sx={{ p: 2, borderBottom: 1, borderColor: "divider" }}>
                    <Typography variant="h4" gutterBottom>
                        Automation Review
                    </Typography>
                    <Stack direction="row" justifyContent="space-between" alignItems="center" spacing={2}>
                        <Stack direction="row" spacing={2}>
                            <Chip label={`FICode: ${FICode}`} color="primary" variant="outlined" />
                            <Chip label={`IRS Income ID: ${irsIncomeId}`} color="primary" variant="outlined" />
                            <Chip label={`Message: ${message}`} color="primary" variant="outlined" />
                        </Stack>
                        <Button
                            variant="contained"
                            color="primary"
                            startIcon={testingAutomation ? <CircularProgress size={16} color="inherit" /> : <Send />}
                            onClick={handleTestAutomation}
                            disabled={testingAutomation || !automationData?.extracted || !automationData?.body}
                        >
                            {testingAutomation ? 'Testing...' : 'Test Automation'}
                        </Button>
                    </Stack>
                    {testResult && (
                        <Alert
                            severity={testResult.success ? "success" : "error"}
                            sx={{ mt: 2 }}
                            onClose={() => setTestResult(null)}
                            icon={testResult.success ? <CheckCircleOutline /> : <ErrorIcon />}
                        >
                            {testResult.message}
                        </Alert>
                    )}
                    {automationData && (
                        <>
                            {automationData.taxYears.length > 0 && (
                                <Stack direction="row" spacing={1} sx={{ mt: 1 }}>
                                    <Typography variant="body2" sx={{ mr: 1 }}>
                                        Tax Years:
                                    </Typography>
                                    {automationData.taxYears.map((year) => (
                                        <Chip key={year} label={year} size="small" color="primary" />
                                    ))}
                                </Stack>
                            )}
                            {automationData.missingYears && automationData.missingYears.length > 0 && (
                                <Stack direction="row" spacing={1} sx={{ mt: 1 }}>
                                    <Typography variant="body2" sx={{ mr: 1 }}>
                                        Missing Years:
                                    </Typography>
                                    {automationData.missingYears.map((year) => (
                                        <Chip
                                            key={year}
                                            label={year}
                                            size="small"
                                            color="error"
                                            icon={<ErrorIcon />}
                                        />
                                    ))}
                                    <Alert severity="error" sx={{ py: 0.5, px: 1 }}>
                                        <Typography variant="caption">
                                            These years were requested but not found in the form
                                        </Typography>
                                    </Alert>
                                </Stack>
                            )}
                            {automationData.page8821 && (
                                <Alert severity="info" sx={{ mt: 1 }}>
                                    <Stack direction="row" alignItems="center" spacing={1}>
                                        <Assignment fontSize="small" />
                                        <Typography variant="body2">
                                            Form 8821 page available for downstream processing
                                        </Typography>
                                        <Chip
                                            label="View in PDF section"
                                            size="small"
                                            variant="outlined"
                                            color="primary"
                                        />
                                    </Stack>
                                </Alert>
                            )}
                        </>
                    )}
                </Box>

                <Grid container sx={{ flex: 1, overflow: "hidden" }}>
                    <Grid size={{ xs: 12, md: 6 }} sx={{ borderRight: 1, borderColor: "divider", overflow: "auto", background: "white" }}>
                        <Box sx={{ p: 2, height: "100%", display: "flex", flexDirection: "column" }}>
                            <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
                                <Typography variant="h6">
                                    {showPage8821 ? 'Form 8821 Page (For Downstream)' : 'Full PDF Document'}
                                </Typography>
                                {page8821Url && (
                                    <Button
                                        size="small"
                                        variant={showPage8821 ? "contained" : "outlined"}
                                        onClick={() => {
                                            setShowPage8821(!showPage8821);
                                            if (!showPage8821) {
                                                setPage8821PageNumber(1);
                                            } else {
                                                setPageNumber(1);
                                            }
                                        }}
                                        startIcon={showPage8821 ? <Description /> : <Assignment />}
                                    >
                                        {showPage8821 ? 'Show Full PDF' : 'Show 8821 Page'}
                                    </Button>
                                )}
                            </Stack>

                            {/* Show Form 8821 Page */}
                            {showPage8821 && page8821Url && documentsReady && (
                                <>
                                    <Alert severity="info" sx={{ mb: 2 }}>
                                        <Typography variant="body2">
                                            This is the extracted Form 8821 page that will be forwarded downstream for processing.
                                        </Typography>
                                    </Alert>
                                    <Box sx={{ mb: 2 }}>
                                        <Typography variant="body2">
                                            Page {page8821PageNumber} of {page8821NumPages || 1}
                                        </Typography>
                                    </Box>
                                    {page8821NumPages > 1 && (
                                        <Box sx={{ display: "flex", justifyContent: "center", mb: 2, gap: 2 }}>
                                            <Button
                                                variant="outlined"
                                                disabled={page8821PageNumber <= 1}
                                                onClick={() => setPage8821PageNumber(page8821PageNumber - 1)}
                                            >
                                                Previous
                                            </Button>
                                            <Button
                                                variant="outlined"
                                                disabled={page8821PageNumber >= page8821NumPages}
                                                onClick={() => setPage8821PageNumber(page8821PageNumber + 1)}
                                            >
                                                Next
                                            </Button>
                                        </Box>
                                    )}
                                    <Box sx={{ flex: 1, overflow: "auto", display: "flex", justifyContent: "center" }}>
                                        <Document
                                            file={page8821Url}
                                            onLoadSuccess={onPage8821LoadSuccess}
                                            options={pdfOptions}
                                        >
                                            <Page
                                                pageNumber={page8821PageNumber}
                                                renderTextLayer={false}
                                                renderAnnotationLayer={false}
                                            />
                                        </Document>
                                    </Box>
                                </>
                            )}

                            {/* Show Full PDF */}
                            {!showPage8821 && pdfUrl && documentsReady && (
                                <>
                                    <Box sx={{ mb: 2 }}>
                                        <Typography variant="body2">
                                            Page {pageNumber} of {numPages}
                                        </Typography>
                                    </Box>
                                    {numPages > 1 && (
                                        <Box sx={{ display: "flex", justifyContent: "center", mb: 2, gap: 2 }}>
                                            <Button
                                                variant="outlined"
                                                disabled={pageNumber <= 1}
                                                onClick={() => setPageNumber(pageNumber - 1)}
                                            >
                                                Previous
                                            </Button>
                                            <Button
                                                variant="outlined"
                                                disabled={pageNumber >= numPages}
                                                onClick={() => setPageNumber(pageNumber + 1)}
                                            >
                                                Next
                                            </Button>
                                        </Box>
                                    )}
                                    <Box sx={{ flex: 1, overflow: "auto", display: "flex", justifyContent: "center" }}>
                                        <Document
                                            file={pdfUrl}
                                            onLoadSuccess={onDocumentLoadSuccess}
                                            options={pdfOptions}
                                        >
                                            <Page
                                                pageNumber={pageNumber}
                                                renderTextLayer={false}
                                                renderAnnotationLayer={false}
                                            />
                                        </Document>
                                    </Box>
                                </>
                            )}
                        </Box>
                    </Grid>

                    <Grid size={{ xs: 12, md: 6 }} sx={{ overflow: "auto", background: "white" }}>
                        <Box sx={{ width: "100%" }}>
                            <Tabs
                                value={tabValue}
                                onChange={(_, newValue) => setTabValue(newValue)}
                                sx={{ borderBottom: 1, borderColor: "divider" }}
                                variant="scrollable"
                                scrollButtons="auto"
                            >
                                <Tab label="Extracted" />
                                <Tab label="Applicant" />
                                <Tab label="Validations" />
                                {automationData.warns && automationData.warns.length > 0 && (
                                    <Tab
                                        label={(
                                            <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                                                Warnings
                                                <Chip
                                                    label={automationData.warns.length}
                                                    size="small"
                                                    color="warning"
                                                />
                                            </Box>
                                        )}
                                    />
                                )}
                                {automationData.errors.length > 0 && (
                                    <Tab
                                        label={(
                                            <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                                                Errors
                                                <Chip
                                                    label={automationData.errors.length}
                                                    size="small"
                                                    color="error"
                                                />
                                            </Box>
                                        )}
                                    />
                                )}
                                {automationData.actionReason && <Tab label="Action" />}
                            </Tabs>

                            <TabPanel value={tabValue} index={0}>
                                {renderExtractedData()}
                            </TabPanel>

                            <TabPanel value={tabValue} index={1}>
                                {renderApplicantInfo(automationData.body)}
                            </TabPanel>

                            <TabPanel value={tabValue} index={2}>
                                {renderValidations()}
                            </TabPanel>

                            {automationData.warns && automationData.warns.length > 0 && (
                                <TabPanel value={tabValue} index={3}>
                                    {renderWarnings()}
                                </TabPanel>
                            )}

                            {automationData.errors.length > 0 && (
                                <TabPanel
                                    value={tabValue}
                                    index={automationData.warns && automationData.warns.length > 0 ? 4 : 3}
                                >
                                    {renderErrors()}
                                </TabPanel>
                            )}

                            {automationData.actionReason && (
                                <TabPanel
                                    value={tabValue}
                                    index={
                                        (automationData.warns && automationData.warns.length > 0 ? 1 : 0)
                                        + (automationData.errors.length > 0 ? 1 : 0) + 3
                                    }
                                >
                                    {renderActionReason()}
                                </TabPanel>
                            )}
                        </Box>
                    </Grid>
                </Grid>
            </Box>
        </Box>
    );
};

export default Automation;
